import React, { useEffect, useState } from 'react';
import { Box } from 'theme-ui';
import { ProductSpotlight } from '../utils';
import QuickshopButton from './Button';
import QuickshopTray from './Tray';
import CardContent from './CardContent';
import QuickshopCardWrapper from './CardWrapper';
import { useBadge } from '~/hooks/components/use-badge';

type Props = {
  spotlight: ProductSpotlight;
  layout: 'horizontal' | 'vertical';
};

export const steps = ['variant', 'add-to-cart', 'added-to-cart', 'out-of-stock', 'stock-request'] as const;
export type Step = (typeof steps)[number];

const QuickshopProductCard = ({ spotlight, layout }: Props) => {
  const { defaultColor, badges } = spotlight;
  const { variants } = spotlight?.content;
  const [selectedColor, setSelectedColor] = useState<Queries.OptionValuesFragmentFragment | null>(defaultColor);
  const [variantImage, setVariantImage] = useState<Queries.ImageFragment | null>(null);
  const [colorQuery, setColorQuery] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<Queries.OptionValuesFragmentFragment | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<Queries.VariantDetailsFragmentFragment | null>(null);
  const [currentStep, setCurrentStep] = useState<Step>(steps[0]);
  const [isTrayOpen, setIsTrayOpen] = useState(false);

  const badge = useBadge({
    selectedOption: selectedVariant ?? selectedColor,
    badges: badges as Queries.BadgeFragment[],
  });
  const handleColorSwatchClick = (optionValue: Queries.OptionValuesFragmentFragment) => {
    const variant = variants?.find((v) => v?.optionValues?.find((o) => o?.slug === optionValue?.slug));
    if (variant) setVariantImage(variant.mainImage);
    const search = new URLSearchParams();
    if (optionValue?.presentation) {
      // If the user cliks on a color swatch, update the URL query string
      search.set('color', optionValue.presentation);
      setColorQuery(search.toString());
    }

    setSelectedColor(optionValue);
  };

  const handleSizeClick = (optionValue: Queries.OptionValuesFragmentFragment) => {
    setSelectedSize(optionValue);
  };

  useEffect(() => {
    if (!selectedColor || !selectedSize) {
      setSelectedVariant(null);
    }

    const variant = variants?.find(
      (variant) =>
        variant?.optionValues?.some((o) => o?.slug === selectedColor?.slug) &&
        variant?.optionValues?.some((o) => o?.slug === selectedSize?.slug)
    );
    if (variant) {
      setSelectedVariant(variant);
      setCurrentStep(steps[1]);
    }
  }, [selectedColor, selectedSize, variants]);

  const handleQuickshopButtonClick = () => {
    setIsTrayOpen(!isTrayOpen);
    setCurrentStep(steps[0]);
  };

  return (
    <QuickshopCardWrapper
      spotlight={spotlight}
      layout={layout}
      colorQuery={colorQuery}
      variantImage={variantImage}
      badge={badge}
    >
      <CardContent
        spotlight={spotlight}
        handleColorSwatchClick={handleColorSwatchClick}
        selectedColor={selectedColor}
        isTrayOpen={isTrayOpen}
        regularPrice={spotlight?.content?.variants?.[0]?.price}
        price={spotlight?.content?.variants?.[0]?.price}
      />

      <Box sx={{ gridArea: 'cta' }}>
        <QuickshopButton handleClick={handleQuickshopButtonClick} isTrayOpen={isTrayOpen} />
        <QuickshopTray
          isOpen={isTrayOpen}
          setIsOpen={setIsTrayOpen}
          product={spotlight.content}
          handleSizeClick={handleSizeClick}
          selectedSize={selectedSize}
          selectedColor={selectedColor}
          selectedVariant={selectedVariant}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          setSelectedSize={setSelectedSize}
          setSelectedColor={setSelectedColor}
          setVariantImage={setVariantImage}
        />
      </Box>
    </QuickshopCardWrapper>
  );
};

export default QuickshopProductCard;
